/**
 * Permission codes the General Ledger screens declare an interest in.
 *
 * They are the dot-strings `internal/gl/permissions.go` and
 * `internal/glreport/permissions.go` register with the RBAC catalogue
 * (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once here means a
 * renamed permission is a compile error rather than a silently invisible menu.
 *
 * `gl.journal.create` and `gl.journal.post` are a declared segregation-of-duties
 * pair (§6): the role that raises a manual journal must not also be the role
 * that puts it in the ledger. The document screen therefore renders Save and
 * Post as two independently permissioned actions and never assumes one implies
 * the other.
 */

export const GL_PERMISSIONS = {
  journalRead: 'gl.journal.read',
  journalCreate: 'gl.journal.create',
  journalUpdate: 'gl.journal.update',
  journalDelete: 'gl.journal.delete',
  journalPost: 'gl.journal.post',
  journalReverse: 'gl.journal.reverse',

  recurringRead: 'gl.recurring.read',
  recurringManage: 'gl.recurring.manage',
  recurringRun: 'gl.recurring.run',

  reportRead: 'gl.report.read',
  reportExport: 'gl.report.export',

  /* ------------------------------------------- C3 posting governance (§6.1) */

  journalTypeRead: 'gl.journal_type.read',
  journalTypeManage: 'gl.journal_type.manage',

  /** Per-account required / optional / forbidden dimensions. */
  dimensionPolicyManage: 'gl.dimension_policy.manage',

  suspenseRead: 'gl.suspense.read',
  suspenseManage: 'gl.suspense.manage',
  /** Clearing or writing an item off moves the ledger; a separate permission. */
  suspenseClear: 'gl.suspense.clear',

  accrualRead: 'gl.accrual.read',
  accrualManage: 'gl.accrual.manage',
  accrualReverse: 'gl.accrual.reverse',

  assertionRead: 'gl.assertion.read',
  /** Accepting a failing completeness assertion is a CFO-level judgement. */
  assertionAccept: 'gl.assertion.accept',

  /** Read the chart of accounts to offer accounts as line choices. */
  accountRead: 'org.account.read',
  /** Create one from the line lookup when the code being typed does not exist. */
  accountCreate: 'org.account.create',
  /** Read companies and fiscal periods to scope a document or a report. */
  companyRead: 'org.company.read',
  fiscalRead: 'org.fiscal.read',
  /** Read the dimension trees behind the per-line cost centre / branch pickers. */
  unitRead: 'org.unit.read',
} as const

export type GlPermission = (typeof GL_PERMISSIONS)[keyof typeof GL_PERMISSIONS]
